import { useEffect, useState } from "react";
import { useNotify } from "react-admin";
import dataProvider from "server/dataProvider";
import { CourseChoices } from "utils/choices";
import { studentRequests } from "api/studentApi";
import { administrationRequests } from "api/administrationApi";
import useChoices from "./useChoices";

const EMPTY = {
  academicYearId: "",
  structuralUnitId: "",
  specialityId: "",
  instructionLanguageId: "",
  course: "",
  curriculumId: "",
  cyclicStreamId: "",
};

// Changing a field clears every field that depends on it. The stream choices are keyed on year, unit, language,
// course and curriculum, so each of them clears the stream.
const DEPENDENTS = {
  academicYearId: ["cyclicStreamId"],
  structuralUnitId: ["specialityId", "instructionLanguageId", "course", "curriculumId", "cyclicStreamId"],
  specialityId: ["instructionLanguageId", "course", "curriculumId", "cyclicStreamId"],
  instructionLanguageId: ["course", "cyclicStreamId"],
  course: ["cyclicStreamId"],
  curriculumId: ["cyclicStreamId"],
};

/**
 * Draft + options of the schedule pre-filter (weekly schedule, cyclic disciplines, cyclic exams), for a list to pass
 * straight to <CustomFilter>:
 *
 *   const schedule = useScheduleFilter({ filters, hasCourse: true });
 *   <CustomFilter filters={schedule.fields} buttonDisabled={!schedule.isComplete}
 *     buttonOnClick={() => setFilters(schedule.query)} />
 *
 * `filters` is the list's committed useStore object - it seeds the draft. `hasCourse` adds curriculum + course,
 * `hasStream` the cyclic stream. A field with a single option is picked automatically; the first year is preselected.
 * Returns `fields` (CustomFilter's filter definitions), `isComplete` (every required field picked) and `query`
 * (the draft without empty values, ready to commit).
 */
const useScheduleFilter = ({ filters, hasCourse = false, hasStream = false }) => {
  const notify = useNotify();
  const [draft, setDraft] = useState({ ...EMPTY, ...filters });

  const [academicYears, setAcademicYears] = useState([]);
  const [units, setUnits] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [curriculums, setCurriculums] = useState([]);
  const [cyclicStreams, setCyclicStreams] = useState([]);

  const { academicYearId, structuralUnitId, specialityId, instructionLanguageId, course, curriculumId } = draft;
  const isFullFilters =
    structuralUnitId && academicYearId && specialityId && curriculumId && instructionLanguageId && course;

  const changeField = (field, value) =>
    setDraft(prev => ({
      ...prev,
      [field]: value,
      ...Object.fromEntries((DEPENDENTS[field] ?? []).map(dependent => [dependent, ""])),
    }));

  // Initial load blocks the whole filter, so a failure gets a contextual message.
  useEffect(() => {
    Promise.all([
      dataProvider.customRequest(studentRequests.getAcademicYearsSelect(), null, "GET"),
      dataProvider.customRequest(administrationRequests.getStructuralUnitsSelect(), null, "GET"),
    ])
      .then(([years, su]) => {
        setAcademicYears(years.data ?? []);
        setUnits(su.data ?? []);
        setDraft(prev =>
          prev.academicYearId || !years.data?.length ? prev : { ...prev, academicYearId: years.data[0].id },
        );
      })
      .catch(() => notify("Грешка при зареждане на начални данни", { type: "error" }));
  }, [notify]);

  useChoices(
    Boolean(structuralUnitId),
    administrationRequests.getSpecialitiesByStructuralUnitIdSelect(structuralUnitId),
    setSpecialities,
    "Грешка при зареждане на специалности",
    notify,
  );
  useChoices(
    Boolean(specialityId),
    administrationRequests.getInstructionLanguagesBySpecialityIdSelect(specialityId),
    setLanguages,
    "Грешка при зареждане на езици",
    notify,
  );
  useChoices(
    Boolean(specialityId),
    studentRequests.getCurriculumsSelect({ specialityId }),
    setCurriculums,
    "Грешка при зареждане на учебни планове",
    notify,
  );
  useChoices(
    Boolean(hasStream && isFullFilters),
    studentRequests.getCyclicStreamsSelect({
      academicYearId,
      structuralUnitId,
      instructionLanguageId,
      course,
      curriculumId,
    }),
    setCyclicStreams,
    "Грешка при зареждане на Поток",
    notify,
  );

  // A single option is picked automatically.
  useEffect(() => {
    const single = [
      ["specialityId", specialities],
      ["curriculumId", curriculums],
      ["instructionLanguageId", languages],
      ["cyclicStreamId", cyclicStreams],
    ].filter(([field, choices]) => choices.length === 1 && !draft[field]);
    if (single.length) {
      setDraft(prev => ({ ...prev, ...Object.fromEntries(single.map(([field, choices]) => [field, choices[0].id])) }));
    }
  }, [specialities, curriculums, languages, cyclicStreams, draft]);

  const select = (label, field, choices) => ({
    label,
    type: "select",
    value: draft[field] ?? "",
    setValue: value => changeField(field, value),
    choices,
  });

  const fields = [
    select("Академична година", "academicYearId", academicYears),
    select("Факултет", "structuralUnitId", units),
    ...(specialities.length ? [select("Специалност", "specialityId", specialities)] : []),
    ...(hasCourse && specialityId && curriculums.length ? [select("Учебен план", "curriculumId", curriculums)] : []),
    ...(languages.length ? [select("Език", "instructionLanguageId", languages)] : []),
    ...(hasCourse && instructionLanguageId ? [select("Курс", "course", CourseChoices)] : []),
    ...(hasStream && course ? [select("Поток", "cyclicStreamId", cyclicStreams)] : []),
  ];

  const isComplete = Boolean(
    academicYearId &&
    structuralUnitId &&
    specialityId &&
    instructionLanguageId &&
    (!hasCourse || (course && curriculumId)) &&
    (!hasStream || draft.cyclicStreamId),
  );

  const query = Object.fromEntries(Object.entries(draft).filter(([, value]) => value !== "" && value != null));

  return { fields, isComplete, query };
};

export default useScheduleFilter;
